import { useEffect, useState } from "react";
import { Download, Check, Share } from "lucide-react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export function InstallAppButton() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      // @ts-expect-error iOS Safari
      window.navigator.standalone === true;
    if (standalone) {
      setInstalled(true);
      return;
    }
    const ua = window.navigator.userAgent;
    setIsIOS(/iphone|ipad|ipod/i.test(ua) && !/crios|fxios/i.test(ua));

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleClick = async () => {
    if (deferred) {
      await deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferred(null);
      return;
    }
    if (isIOS) {
      setShowIOSHint((v) => !v);
    }
  };

  if (installed) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Check size={14} className="text-accent" /> Appen är installerad
      </span>
    );
  }

  // On non-iOS browsers without the prompt (e.g. Firefox desktop), hide the button.
  if (!deferred && !isIOS) return null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleClick}
        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background/50 px-3 py-1.5 text-xs font-medium text-foreground transition hover:border-accent hover:text-accent"
      >
        <Download size={14} className="text-accent" /> Installera appen
      </button>
      {showIOSHint && isIOS && (
        <div className="absolute bottom-full left-1/2 z-10 mb-2 w-64 -translate-x-1/2 rounded-lg border border-border bg-background p-3 text-xs leading-relaxed text-foreground shadow-lg">
          <p className="flex items-center gap-1.5 font-medium">
            <Share size={14} className="text-accent" /> På iPhone/iPad
          </p>
          <p className="mt-1.5 text-muted-foreground">
            Tryck på <b>Dela</b>-knappen i Safari och välj <b>Lägg till på hemskärmen</b>.
          </p>
        </div>
      )}
    </div>
  );
}
